import { useEffect, useState } from 'react';

const FONTS_MAX_WAIT_MS = 1500;   // never let a slow font CDN hold the UI hostage
const AUTH_MAX_WAIT_MS = 2500;    // cap on waiting for /auth/me before revealing anyway

const raf = () => new Promise((r) => requestAnimationFrame(() => r()));

/**
 * Reveal gate for the React shell. Returns true once:
 *  - the bundle CSS has been applied and a layout pass has happened (double rAF),
 *  - web fonts have settled (or the wait cap elapsed), and
 *  - the initial session check finished when a session is likely (so a logged-in
 *    user never sees the public header swap to the sidebar), capped by AUTH_MAX_WAIT_MS.
 */
export function useUiReady(authLoading) {
  const [assetsReady, setAssetsReady] = useState(false);
  const [authWaitExpired, setAuthWaitExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fonts = document.fonts?.ready ?? Promise.resolve();
    const cap = new Promise((r) => setTimeout(r, FONTS_MAX_WAIT_MS));
    Promise.race([fonts, cap])
      .then(raf).then(raf)
      .then(() => { if (!cancelled) setAssetsReady(true); });
    const t = setTimeout(() => setAuthWaitExpired(true), AUTH_MAX_WAIT_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  const sessionLikely = localStorage.getItem('has_session') === 'true';
  const authSettled = !authLoading || !sessionLikely || authWaitExpired;
  return assetsReady && authSettled;
}

/** Fade out and remove the pre-React #boot-splash from public/index.html. */
export function dismissBootSplash() {
  const el = document.getElementById('boot-splash');
  if (!el || el.classList.contains('is-done')) return;
  el.classList.add('is-done');
  setTimeout(() => el.remove(), 320);
}
